import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SERVICE_ICONS } from "@/lib/company";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle } from "@/components/admin/AdminLayout";

const BLANK = { title: "", description: "", icon: "truck", order: 0, active: true };

const ServiceDialog = ({ value, onClose, onSaved }) => {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { title: f.title, description: f.description, icon: f.icon, order: Number(f.order) || 0, active: f.active };
      if (f.id) await api.put(`/admin/services/${f.id}`, payload); else await api.post("/admin/services", payload);
      toast.success("Servizio salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none" data-testid="service-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Titolo</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 rounded-none" data-testid="service-title-input" /></div>
          <div><Label>Descrizione</Label><Textarea required rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 rounded-none" data-testid="service-description-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Icona</Label>
              <Select value={f.icon} onValueChange={(v) => setF({ ...f, icon: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="service-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SERVICE_ICONS).map(([k, I]) => <SelectItem key={k} value={k}><span className="flex items-center gap-2"><I className="h-4 w-4" /> {k}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Ordine</Label><Input type="number" min={0} value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="mt-1 rounded-none" data-testid="service-order-input" /></div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="service-active-switch" /><Label>Visibile sul sito</Label></div>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-none bg-[#E50914] hover:bg-[#C10710]" data-testid="service-save-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function ServicesAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (s) => {
    if (!window.confirm(`Eliminare il servizio "${s.title}"?`)) return;
    await api.delete(`/admin/services/${s.id}`);
    toast.success("Servizio eliminato");
    load();
  };
  return (
    <div data-testid="admin-services">
      <PageTitle title="Servizi del sito" subtitle="Contenuti mostrati nelle pagine Home e I nostri servizi">
        <Button className="rounded-none bg-[#E50914] hover:bg-[#C10710]" onClick={() => setEditing({ ...BLANK, order: items?.length || 0 })} data-testid="service-add-btn"><Plus className="h-4 w-4 mr-1" /> Aggiungi servizio</Button>
      </PageTitle>
      {!items ? <Loader2 className="h-6 w-6 animate-spin text-[#E50914]" /> : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {items.map((s) => {
            const Icon = SERVICE_ICONS[s.icon] || SERVICE_ICONS.truck;
            return (
              <div key={s.id} className="flex items-start gap-4 p-4 sm:p-5" data-testid={`service-row-${s.id}`}>
                <div className="h-10 w-10 flex items-center justify-center bg-[#E50914]/10 text-[#E50914] shrink-0"><Icon className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold">{s.title} {!s.active && <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-200 px-1.5 py-0.5">Nascosto</span>}</h3>
                  <p className="text-sm text-slate-600 mt-0.5">{s.description}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditing(s)} data-testid={`service-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="rounded-none text-red-600" onClick={() => remove(s)} data-testid={`service-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing && <ServiceDialog value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
